import { useEffect, useMemo, useState } from "react";
import { lerHistorico, type LinhaDoHistorico } from "../lib/faculdade";
import { AvisoNavy, Selo, TituloDaSecao, data } from "./FaculdadeUi";
import Carregando from "./Carregando";

// Histórico de tentativas — o que o aluno já fez, em ordem de data.
//
// Pedido da reunião de 31/08 com o time da secretaria, nestas palavras:
// *"mostrar o histórico de tentativas em uma aba separada (avaliação, nota,
// tentativa, data)"*.
//
// ---------------------------------------------------------------------------
// Por que é uma ABA e não uma seção de "Minhas notas"
// ---------------------------------------------------------------------------
// As duas telas respondem perguntas diferentes, e cada uma tem uma FORMA certa:
// o boletim é "como estou em cada avaliação" — uma linha por disciplina, a MAIOR
// nota, com o botão de fazer a prova; o histórico é "o que eu já fiz" — uma linha
// por tentativa, em ordem de data, sem ação nenhuma. Juntar as duas numa tela só
// daria a mesma informação duas vezes com rótulos diferentes, que é a cicatriz do
// IR Pro (8.249px para 1.120px com o MESMO conteúdo).
//
// E aba é SUB-ROTA (`/faculdade/historico`): o aluno compartilha o link, volta
// pelo botão do navegador e recarregar não o joga para a primeira.
//
// ---------------------------------------------------------------------------
// Por que "mostrar mais", e não a lista inteira
// ---------------------------------------------------------------------------
// Medido em 01/09 sobre 23.218 alunos com tentativa: mediana **6**, p90 **25**,
// p99 **49** e máximo **150**. Uma tabela de 150 linhas passa de 7.000px — e a
// aba Secretaria já saiu com 141.111px numa versão anterior, por vazamento de
// lista. 25 por vez cobre 90% dos alunos numa tela só e mantém a página curta
// para os 88 que passam de 60. É o mesmo padrão da fila do admin, que caiu de
// 41.910px para 5.151px assim.
//
// ---------------------------------------------------------------------------
// É TABELA de verdade, e não cards
// ---------------------------------------------------------------------------
// Quatro colunas curtas que se comparam entre linhas — nota da tentativa 1 contra
// a da tentativa 2 é a leitura natural. Card por tentativa daria 120px para
// mostrar 30 caracteres, que é exatamente o que a primeira tela do IR Pro fazia.
// A tabela rola dentro de um `overflow-x-auto` próprio: o corpo da página nunca
// rola na horizontal.

const POR_PAGINA = 25;

export default function FaculdadeHistorico({ cursoAtivo }: { cursoAtivo: string }) {
  const [fase, setFase] = useState<"carregando" | "pronto" | "erro">("carregando");
  const [linhas, setLinhas] = useState<LinhaDoHistorico[]>([]);
  const [mostrando, setMostrando] = useState(POR_PAGINA);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const h = await lerHistorico();
        if (!vivo) return;
        setLinhas(h);
        setFase("pronto");
      } catch {
        if (vivo) setFase("erro");
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  // Trocar de curso volta ao começo da lista: continuar na página 4 de outro
  // curso mostraria uma tela vazia com um botão de "mostrar mais" inútil.
  useEffect(() => {
    setMostrando(POR_PAGINA);
  }, [cursoAtivo]);

  const doCurso = useMemo(
    () => linhas.filter((l) => l.curso_slug === cursoAtivo),
    [linhas, cursoAtivo],
  );

  if (fase === "carregando") return <Carregando />;
  if (fase === "erro") {
    return (
      <AvisoNavy titulo="Não conseguimos carregar o seu histórico" tom="erro">
        Recarregue a página. Se continuar, fale com a secretaria — as suas tentativas continuam
        guardadas.
      </AvisoNavy>
    );
  }

  if (doCurso.length === 0) {
    return (
      <AvisoNavy titulo="Você ainda não fez nenhuma prova neste curso">
        Quando fizer, cada tentativa aparece aqui com a nota e a data — inclusive as que você
        refizer.
      </AvisoNavy>
    );
  }

  const visiveis = doCurso.slice(0, mostrando);
  const faltam = doCurso.length - visiveis.length;

  return (
    <>
      <TituloDaSecao
        nota={
          doCurso.length === visiveis.length
            ? `${doCurso.length} tentativa${doCurso.length === 1 ? "" : "s"}`
            : `${visiveis.length} de ${doCurso.length} tentativas`
        }
      >
        Todas as suas tentativas
      </TituloDaSecao>

      {/* A explicação vem antes da tabela porque muda como se lê a coluna "Nota":
          a nota que VALE é a maior, e não a última. Sem isso, um aluno que
          refez e tirou menos lê a última linha como a nota dele. */}
      <p className="mb-4 max-w-2xl text-sm text-navy-mute">
        Cada linha é uma tentativa, da mais recente para a mais antiga. Em cada avaliação vale a{" "}
        <strong className="font-semibold text-navy-ink">maior</strong> nota, e é ela que aparece em{" "}
        <strong className="font-semibold text-navy-ink">Avaliações</strong>.
      </p>

      {/* `overflow-x-auto` no invólucro da tabela, não no corpo da página: quatro
          colunas com nome de disciplina não cabem em 360px, e a página inteira
          rolando na horizontal é defeito. */}
      <div className="tv-card overflow-x-auto">
        <table data-historico className="w-full min-w-[36rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-navy-ink/15 text-left">
              <th scope="col" className="p-4 font-display font-semibold text-white">
                Avaliação
              </th>
              <th scope="col" className="p-4 font-display font-semibold text-white">
                Tentativa
              </th>
              <th scope="col" className="p-4 text-right font-display font-semibold text-white">
                Nota
              </th>
              <th scope="col" className="p-4 font-display font-semibold text-white">
                Data
              </th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((l) => (
              <Linha key={l.id} linha={l} />
            ))}
          </tbody>
        </table>
      </div>

      {faltam > 0 && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            data-mostrar-mais
            onClick={() => setMostrando((m) => m + POR_PAGINA)}
            className="btn-contorno-navy"
          >
            Mostrar mais {Math.min(faltam, POR_PAGINA)} de {faltam}
          </button>
        </div>
      )}
    </>
  );
}

function Linha({ linha }: { linha: LinhaDoHistorico }) {
  return (
    // `data-tentativa` é escopo de prova: numa tabela real o mesmo texto aparece
    // em várias linhas (a mesma disciplina em cinco tentativas), e seletor por
    // texto sem escopo pega a errada — a cicatriz dos sete rótulos iguais.
    <tr data-tentativa={linha.id} className="border-b border-navy-ink/10 last:border-0">
      <td className="p-4">
        <span className="font-semibold text-white">{linha.disciplina}</span>
        {/* Fora da grade: 2.659 tentativas reais estão nesse estado (1,1%), de
            quem trocou de turma. Dizer isso na linha evita o aluno concluir que
            a tela inventou uma disciplina. */}
        {!linha.na_grade && (
          <span className="mt-1 block text-xs text-navy-mute">Fora da grade da sua turma</span>
        )}
      </td>
      <td className="p-4 tabular-nums text-navy-ink">
        {linha.numero}
        <sup className="ml-0.5 text-[0.65em]">ª</sup>
      </td>
      <td className="p-4 text-right">
        {linha.nota === null ? (
          // Tentativa aberta ou abandonada não tem nota, e um zero aqui seria
          // mentira sobre quem não terminou.
          <span className="text-navy-mute">—</span>
        ) : (
          <span
            data-nota-tentativa={linha.nota}
            className="font-display text-base font-bold tabular-nums text-white"
          >
            {linha.nota.toLocaleString("pt-BR", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            })}
          </span>
        )}
        {/* `aprovada` é NULO quando a tentativa não terminou — ali "aprovada" não
            é dizível, e o banco devolve nulo em vez de false justamente por
            isso. Um selo "Reprovado" numa prova em curso seria falso. */}
        {linha.aprovada !== null && (
          <span className="ml-2 align-middle">
            <Selo tom={linha.aprovada ? "ok" : "erro"}>
              {linha.aprovada ? "Passou" : "Não passou"}
            </Selo>
          </span>
        )}
      </td>
      <td className="p-4 whitespace-nowrap text-navy-ink">
        {linha.situacao === "em_andamento" ? (
          <Selo tom="espera">Em andamento</Selo>
        ) : linha.situacao === "abandonada" ? (
          // Abandonada CONSOME tentativa, e é isso que o aluno precisa saber ao
          // ver o número dele cair sem nota nenhuma no lugar.
          <span>
            <span className="block">{data(linha.iniciada_em)}</span>
            <span className="mt-1 block text-xs text-navy-mute">Não finalizada</span>
          </span>
        ) : (
          data(linha.finalizada_em)
        )}
      </td>
    </tr>
  );
}
